import React, {useEffect, useMemo, useState} from "react";
import {LazyLoadImage as Lazy} from "react-lazy-load-image-component";
import DefaultNoImage from "../../../../assets/images/no-image.svg";
import EnvService from "../../../../core/services/env";
import Utils from "../../../../core/services/utils/utils";

/**
 *
 * @param {any?} srcProp
 * @param {string?} alt
 * @param {ReactNode?} noImage
 * @param {number | string | undefined | function} heightProps
 * @param {import('react-lazy-load-image-component').LazyLoadImageProps} props
 */
const LazyImage = ({
                       src: srcProp,
                       alt = '',
                       noImage: noImageProps,
                       height: heightProps,
                       debug = false,
                       onLoad = () => void 0,
                       onError = () => void 0,
                       ...props
                   }) => {
    const [src, setSrc] = useState(() => Utils.encodeFileUrl(srcProp));
    const noImage = useMemo(() => noImageProps ?? DefaultNoImage, [noImageProps])
    const height = useMemo(() => typeof heightProps === 'function' ? heightProps() : heightProps, [heightProps])

    /**
     * Listens for the changes in srcProp and with each change:
     * sets the encoded src of the state.
     */
    useEffect(() => {
        setSrc(Utils.encodeFileUrl(srcProp));
    }, [srcProp])

    /**
     * Changes the src of the image to noImage if there is noImage available and the src is not already noImage, and it's not a valid React element
     * If the noImage is a valid component, sets the [src] in the state to null
     *
     * @param {Event} e
     */
    const onImageError = (e) => {
        if (EnvService.isDevelopment)
            console.log("Image loading failed", src, (noImage))

        if (onError) onError(e);

        if (src === noImage)
            return;
        setSrc(React.isValidElement(noImage) ? null : noImage)
    }

    /**
     * Adds the loaded class name to the wrapper of the image as soon as the image loads.
     * @param {Event} e
     */
    const onImageLoaded = (e) => {
        const span = e.target?.parentElement;
        if (span) {
            span.classList.add('lazy-load-image-loaded');
        }
        if (onLoad) {
            onLoad(e);
        }
    }

    return (
        <>
            {
                src
                    ? <Lazy
                        src={src}
                        effect="opacity"
                        alt={alt}
                        {...(
                            onLoad
                                ? {onLoad: onImageLoaded}
                                : {}
                        )}
                        onError={onImageError}
                        height={height}
                        {...props}
                    />
                    : React.isValidElement(noImage)
                        ? noImage
                        : <Lazy
                            src={DefaultNoImage}
                            effect="opacity"
                            alt="placeholder"
                            height={height}
                            {...(
                                onLoad
                                    ? {onLoad: onImageLoaded,}
                                    : {}
                            )}
                            {...props}
                        />
            }
        </>
    );
};

export default LazyImage;
